<template>
    <div 
        style="visibility: hidden;" 
        class="page" 
        data-loaded="false" 
        :id="pageContainerId" 
        data-page-number="pageNumber"
        v-on:mousedown="mouseDown"
        v-on:mouseup="mouseUp"
        @contextmenu="preventContext($event)"
    >
        <div class="canvasWrapper">
            <canvas :id="`page${pageNumber}`" :height="pageHeight" :width="pageWidth" ></canvas>
        </div>
        <div class="textLayer"></div>
    </div>
</template>

<script>
// Vendor
import Utils from '@/lib/Utils';
import { PDFJS } from 'pdfjs-dist/web/pdf_viewer';
import { mapGetters } from 'vuex';

// Component
PDFJS.workerSrc = '../../../node_modules/pdfjs-dist/build/pdf.worker.js';

const ProjectPdfPage = {
    name: 'ProjectPdfThumbnailPage',
    props: ['page', 'pageWidth', 'pageHeight', 'currentLayer', 'issrc'],
    data: function() {
        return {
            pdfRender: null,
            pageNumber: this.$props.page.pageNumber,
            pdfPage: this.$props.page.pdfPage,
            selectedPageNumder: '',
            pageContainerId: 'pageContainer' + this.$props.page.pageNumber
        };
    },
    computed: {
        ...mapGetters([
            'user',
            'selectedTool',
            'selectedToolIndex',
            'activeProjectEntitled'
        ])
    },
    methods: {
        renderPage: function() {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const canvasContext = canvas.getContext('2d', {alpha: false});
            // Deep copy of the viewport so we don't get changes halfway through.
            const viewport = Utils.getViewport(this.pdfPage, canvas.width, canvas.height);
            const transform = Utils.scalePage(this.$el, viewport, canvasContext);
            // Render the page
            this.pdfRender = this.pdfPage.render({ canvasContext, viewport, transform })
            .then(() => {
                // Indicate that the page was loaded
                this.$el.dataset.loaded = 'true';
                return this.pdfPage.getTextContent({normalizeWhitespace: true})
                .then((textContent) => {
                    const textLayer = this.$el.querySelector('.textLayer');
                    textLayer.innerHTML = '';
                    const textLayerFactory = new PDFJS.DefaultTextLayerFactory();
                    const textLayerBuilder = textLayerFactory.createTextLayerBuilder(
                        textLayer, this.pageNumber, viewport, true);
                    textLayerBuilder.setTextContent(textContent);
                    textLayerBuilder.render();
                    this.$emit('pdfLoaded', 
                        {pageText: textContent, pageNumber: this.pageNumber, viewport: viewport});
                        
                    document.querySelector('.page-viewer').style.width = 'auto';
                    document.querySelector('.page-viewer').style.margin = '0';
                });
            });
        },        
        pageSelected(pageNumber) {
            this.selectedPageNumder = pageNumber;
        },
        selectedPageStyle(pageNumber) {
            if (this.selectedPageNumder === pageNumber) {
                return {
                    'border-color': 'green'
                };
            }
            return { 'border-color': 'unset' };
        },
        mouseDown: function(e) {
            if (e.button === 0 || (e.button === 0 && e.ctrlKey === true)) {
                e.preventDefault();
                if (!this.user.active_pay) {
                    if (!this.issrc && !this.activeProjectEntitled) {
                        this.$emit('openDialog', 'no-collaborator-dialog');
                        return;
                    }
                }
                switch (this.selectedTool) {
                case 'text':
                    this.createTextNote(e);
                    break;
                case 'highlight':
                    this.createHighlightNote(e);
                    break;
                case 'line':
                    this.createLineNote(e);
                    break;
                case 'stamp':
                    this.createStampNote(e);
                    break;
                case 'count':
                    this.createCountNote(e);
                    break;
                case 'image':
                    this.createImage(e);
                    break;
                case 'cueSelect':
                    if (!this.currentCueLayer) {
                        this.$emit('openDialog', 'no-layers-dialog');
                        return;
                    }
                    this.createSelectionCuePlacement(e);
                    break;
                default:
                    // do nothing
                    break;
                }
            }
        },
        mouseUp: function(e) {
            if (e.button === 2) {
                // do nothing
            }
        },
        createSelectionCuePlacement: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();
            const cue = {
                type: 'position',
                width: 60,
                height: 20,
                x: e.clientX - bounds.x - 30,
                y: e.clientY - bounds.y - 10
            };
            this.$emit('cueCreated', cue);
        },
        createSelectionCue: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();

            const cue = {
                type: 'selection',
                width: 60,
                height: 20,
                x: e.clientX - bounds.x - 30,
                y: e.clientY - bounds.y - 10
            };

            this.$emit('cueCreated', cue);
        },
        createPositionCue: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();
            const cue = {
                type: 'position',
                x: e.clientX - bounds.x,
                y: e.clientY - bounds.y,
                width: 6,
                height: 6
            };
            this.$emit('cueCreated', cue);
        },
        preventContext: function(e) {
            e.preventDefault();
            e.stopPropagation();
            return false;
        },
        createTextNote: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();

            const data = {
                x: e.clientX - bounds.x,
                y: e.clientY - bounds.y
            };

            this.$emit('addNote', {
                data,
                type: 'text'
            });
        },
        createHighlightNote: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();

            const data = {
                width: 100,
                height: 24,
                x: e.clientX - bounds.x - 50,
                y: e.clientY - bounds.y - 12
            };

            this.$emit('addNote', {
                data,
                type: 'highlight'
            });
        },
        createLineNote: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();

            const data = {
                width: 300,
                height: 2,
                x: e.clientX - bounds.x,
                y: e.clientY - bounds.y
            };

            this.$emit('addNote', {
                data,
                type: 'line'
            });
        },
        createStampNote: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();

            const data = {
                width: 40,
                height: 40,
                x: e.clientX - bounds.x,
                y: e.clientY - bounds.y,
                subType: this.selectedToolIndex
            };

            this.$emit('addNote', {
                data,
                type: 'stamp'
            });
        },
        createImage: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();

            const data = {
                width: 40,
                height: 40,
                x: e.clientX - bounds.x,
                y: e.clientY - bounds.y
            };

            this.$emit('addNote', {
                data,
                type: 'image'
            });
        },
        createCountNote: function(e) {
            const canvas = this.$el.querySelector('.canvasWrapper canvas');
            const bounds = canvas.getBoundingClientRect();

            const data = {
                width: 100,
                height: 24,
                x: e.clientX - bounds.x,
                y: e.clientY - bounds.y,
                subType: this.selectedToolIndex
            };

            switch (this.selectedToolIndex) {
            case '0':
                data.text = '1 2 3 4';
                break;
            case '1':
                data.text = '1 2 3 4 5 6';
                break;
            case '2':
                data.text = '1 2 3 4 5 6 7 8';
                break;
            default:
                break;
            }

            this.$emit('addNote', {
                data,
                type: 'count'
            });
        }
    },
    mounted: function() {
        this.renderPage();
    }
};

export default ProjectPdfPage;
</script>

<style lang="scss">
.textLayer {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  opacity: 0.2;
  line-height: 1.0;
}

.textLayer > div {
  color: transparent;
  position: absolute;
  white-space: pre;
  cursor: text;
  -webkit-transform-origin: 0% 0%;
  -moz-transform-origin: 0% 0%;
  -o-transform-origin: 0% 0%;
  -ms-transform-origin: 0% 0%;
  transform-origin: 0% 0%;
}

.textLayer::selection { background: rgb(0,0,255); }
.textLayer::-moz-selection { background: rgb(0,0,255); }

</style>


// WEBPACK FOOTER //
// pdf-thumbnail-page.vue?7a3e2c88